import type { Database } from '../../database.types';
import { getSupabaseClient, type DdmSupabaseClient } from '../../supabase';
import { escapeIlikeTerm } from '../ilike';

export type AdminBuyerRequestRow = Database['public']['Tables']['buyer_requests']['Row'];
export type AdminBuyerRequestStatus = Database['public']['Enums']['request_status'];
export type AdminBuyerRequestType = Database['public']['Enums']['request_type'];

type BuyerJoin = Pick<
  Database['public']['Tables']['users']['Row'],
  'id' | 'first_name' | 'last_name' | 'email' | 'phone' | 'language_preference' | 'deleted_at'
>;

type DealJoin = Pick<
  Database['public']['Tables']['deals']['Row'],
  'id' | 'title' | 'slug' | 'zone' | 'reference_id'
>;

export type AdminBuyerRequestRowJoined = AdminBuyerRequestRow & {
  users: BuyerJoin | null;
  deals: DealJoin;
};

export type AdminRequestsStatusFilter = 'all' | AdminBuyerRequestStatus;

export type ListAdminRequestsParams = {
  status?: AdminRequestsStatusFilter;
  searchQuery?: string;
  pageSize?: number;
  offset?: number;
};

export type ListAdminRequestsResult = {
  rows: AdminBuyerRequestRowJoined[];
  total: number;
};

const DEFAULT_PAGE_SIZE = 25;

const SELECT_REQUEST_WITH_JOINS =
  '*, users:user_id (id, first_name, last_name, email, phone, language_preference, deleted_at), deals:deal_id (id, title, slug, zone, reference_id)';

const FSM_TRANSITIONS: Record<AdminBuyerRequestStatus, AdminBuyerRequestStatus[]> = {
  new: ['in_progress', 'treated', 'closed'],
  in_progress: ['treated', 'closed'],
  treated: ['closed'],
  closed: [],
};

/**
 * Forward-only transitions allowed from the given status. Mirror of the
 * `buyer_requests_status_forward` SQL function (20260506150000_buyer_engagement.sql
 * lines 260-264). Used by the admin drawer dropdown to only show valid
 * targets — the trigger remains the source of truth and raises 23514 on
 * an attempt that bypasses the UI.
 */
export function nextAllowedStatuses(current: AdminBuyerRequestStatus): AdminBuyerRequestStatus[] {
  return FSM_TRANSITIONS[current];
}

/**
 * List admin-visible buyer requests with optional status filter. The
 * shape includes embedded `users` (buyer contact) and `deals` (deal id +
 * slug + zone). When the buyer was hard-deleted the FK is SET NULL and
 * `users` resolves to `null` — callers must handle that path.
 *
 * RLS: relies on `buyer_requests_buyer_select` from
 * 20260506150000_buyer_engagement.sql lines 425-436 — admin and super_admin
 * see every row regardless of `bitrix24_sync_failed` / status.
 */
export async function listAdminRequests(
  params: ListAdminRequestsParams = {},
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<ListAdminRequestsResult> {
  const pageSize = params.pageSize ?? DEFAULT_PAGE_SIZE;
  const offset = params.offset ?? 0;

  let q = client.from('buyer_requests').select(SELECT_REQUEST_WITH_JOINS, { count: 'exact' });

  if (params.status && params.status !== 'all') {
    q = q.eq('status', params.status);
  }

  if (params.searchQuery && params.searchQuery.trim().length > 0) {
    const term = escapeIlikeTerm(params.searchQuery.trim());
    if (term.length > 0) {
      // ADR-056 §4 — reference_id only: buyer (users) and deal are
      // embedded resources and PostgREST cannot OR a top-level column with
      // a foreign one in a single query. The page's placeholder announces
      // the reference-only contract.
      q = q.ilike('reference_id', `%${term}%`);
    }
  }

  const { data, error, count } = await q
    .order('created_at', { ascending: false })
    .range(offset, offset + pageSize - 1);

  if (error) throw error;
  return {
    rows: (data ?? []) as unknown as AdminBuyerRequestRowJoined[],
    total: count ?? 0,
  };
}

/**
 * GLOBAL count of `new` buyer requests — deliberately independent of the
 * page AND of the active filters (ADR-056): the "new" badge is a triage
 * signal, not a property of the current view. Never derive it from the
 * paginated rows.
 */
export async function countAdminRequestsNew(
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<number> {
  const { count, error } = await client
    .from('buyer_requests')
    .select('id', { count: 'exact', head: true })
    .eq('status', 'new');

  if (error) throw error;
  return count ?? 0;
}

/**
 * Fetch a single buyer request including joins. Returns `null` if no
 * row matches (or RLS hides it). Used by the admin drawer to refresh
 * `status_history` after a mutation.
 */
export async function getAdminRequestById(
  id: string,
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<AdminBuyerRequestRowJoined | null> {
  const { data, error } = await client
    .from('buyer_requests')
    .select(SELECT_REQUEST_WITH_JOINS)
    .eq('id', id)
    .maybeSingle();

  if (error) throw error;
  return (data as unknown as AdminBuyerRequestRowJoined | null) ?? null;
}

/**
 * Transition a request to a new status via plain UPDATE. The DB trigger
 * `buyer_requests_status_forward_trg` (20260506150000_buyer_engagement.sql
 * lines 247-291) enforces forward-only — a forbidden transition surfaces
 * as PostgrestError `code = '23514'` (check_violation), propagated to the
 * caller. The UI matches on that code to show a localized toast.
 *
 * The same UPDATE also fires `buyer_requests_log_status_change_trg`
 * (20260507120000_buyer_requests_audit_log.sql) which writes the
 * `request_status_changed` audit row in the same transaction.
 */
export async function updateBuyerRequestStatus(
  id: string,
  toStatus: AdminBuyerRequestStatus,
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<AdminBuyerRequestRowJoined> {
  const { data, error } = await client
    .from('buyer_requests')
    .update({ status: toStatus })
    .eq('id', id)
    .select(SELECT_REQUEST_WITH_JOINS)
    .single();

  if (error) throw error;
  return data as unknown as AdminBuyerRequestRowJoined;
}

/**
 * Update the admin-only `internal_note` field. The DB CHECK
 * `buyer_requests_internal_note_length` caps the value at 1000 chars; the
 * caller (form) validates with Zod first so we never round-trip a value
 * that the DB will reject. `null` clears the note.
 *
 * RLS: only admin / super_admin can UPDATE (policy
 * `buyer_requests_admin_update`, 20260506150000 lines 455-459).
 * The `buyer_requests_log_status_change_trg` does NOT fire here — its
 * trigger is targeted on the `status` column only (cf. 20260507120000
 * §2 comment).
 */
export async function updateBuyerRequestInternalNote(
  id: string,
  note: string | null,
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<AdminBuyerRequestRowJoined> {
  const { data, error } = await client
    .from('buyer_requests')
    .update({ internal_note: note })
    .eq('id', id)
    .select(SELECT_REQUEST_WITH_JOINS)
    .single();

  if (error) throw error;
  return data as unknown as AdminBuyerRequestRowJoined;
}

/**
 * Server-side CSV export of buyer_requests. Routed through the
 * `export_buyer_requests_csv` RPC (SECURITY DEFINER) so R11 (max 500 rows)
 * + R19 (max 5/min/admin) gates run inside the same transaction that
 * persists the `admin_csv_exports` row + audit_log entry.
 *
 * Errors propagate as Supabase PostgrestError. Caller maps:
 *   - code '42501' → forbidden (not admin)
 *   - code '54000' → rate-limited (R19)
 */
export async function exportBuyerRequestsCsv(
  filter: AdminBuyerRequestStatus | null,
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<string> {
  const { data, error } = await client.rpc('export_buyer_requests_csv', {
    p_status_filter: filter ?? undefined,
  });
  if (error) throw error;
  return (data ?? '') as string;
}
